import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Checkbox, FormGroup, FormHelperText, FormLabel, TextField } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Add, Business, Create, GroupAdd } from '@mui/icons-material';
import { AdminProgressBar } from '../AdminProgressBar';
import { postPatientToLists } from '../../lib/api';
import { AdminCheckbox } from '../AdminCheckbox';
import { AddPatientListDialog } from '../AddPatientListDialog';
import useMetric from '../../lib/hooks/useMetric';
import usePatientLists from '../../lib/hooks/usePatientLists';
import PatientListsIndividualCard from '../../screens/DashboardScreen/components/PatientListsIndividualCard';
import { postPatientList } from '../../lib/api/postPatientList';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    width: 'fit-content',
    '& svg': {
      marginBottom: -6,
      marginRight: 4,
    },
    '& input': {
      borderColor: theme.palette.admin.main,
    },
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: 120,
  },
  formControlLabel: {
    marginTop: theme.spacing(3),
    marginLeft: 0,
    '& svg': {
      marginBottom: -6,
    },
  },
  addPatientList: {
    backgroundColor: theme.palette.admin.main,
    color: theme.palette.admin.light,
    width: 214,
    height: 50,
    marginTop: -25,
    marginRight: 25,
    display: 'block',
    '&:hover': {
      backgroundColor: theme.palette.admin.dark,
      color: theme.palette.admin.light,
    },
  },
  shareSwitch: {
    color: theme.palette.admin.main,
  },
  cancelButton: {
    color: theme.palette.error.main,
  },
  addButton: {
    color: theme.palette.admin.main,
  },
  listBorder: {
    borderWidth: 0,
    borderBottomWidth: 2,
    borderStyle: 'solid',
  },
}));

const AdminGreenSwitch = withStyles((theme) => ({
  switchBase: {
    color: theme.palette.admin.dark,
    '&$checked': {
      color: theme.palette.admin.main,
    },
    '&$checked + $track': {
      backgroundColor: theme.palette.admin.main,
    },
  },
  checked: {},
  track: {},
}))(Switch);
const AdminGreenTextField = withStyles((theme) => ({
  root: {
    borderColor: theme.palette.admin.main,
    color: theme.palette.admin.main,
    '&:hover': {
      borderColor: theme.palette.admin.main,
      color: theme.palette.admin.main,
    },
  },
}))(TextField);

const AddPatientToListDialog = ({ patient, open, handleClose, refetch }) => {
  const classes = useStyles();
  const [saving, setSaving] = useState(false);
  const [listIds, setListIds] = useState([]);

  const { isLoading, isRefetching, data: dataObject, refetch: refetchInternal } = usePatientLists();
  const { data: patientLists } = dataObject ?? [];
  const { isLoading: isLoading2, data } = useMetric('patient_lists', patient.id);

  useEffect(() => {
    if (open && data?.metric?.value) {
      const currentListIds = data.metric.value.map((item) => item.id);
      setListIds(currentListIds);
    }
  }, [open, data, patient.id]);

  const handleChange = useCallback((event) => {
    const changingId = Number(event.target.name);
    setListIds((prev) => (event.target.checked ? [...prev, changingId] : prev.filter((item) => item !== changingId)));
  }, []);

  const handleSave = useCallback(() => {
    setSaving(true);
    postPatientToLists(listIds, patient.id).then(() => {
      setSaving(false);
      if (typeof refetch === 'function') {
        refetch();
      } else {
        refetchInternal();
      }
      handleClose();
    });
  }, [listIds, patient, refetch, refetchInternal, handleClose]);

  return (
    <>
      <Dialog
        maxWidth={'sm'}
        fullWidth={true}
        open={open}
        onClose={handleClose}
        aria-labelledby="add-patient-to-list-dialog"
        className={classes.form}
      >
        <DialogTitle id="add-patient-to-list-dialog-title">
          <GroupAdd /> {saving ? 'Saving ' : 'Manage '} {patient.firstName + ' ' + patient.lastName}&apos;s Lists
        </DialogTitle>
        <DialogContent>
          {saving || isLoading || isLoading2 ? (
            <AdminProgressBar style={{ margin: '50px auto 50px' }} />
          ) : (
            <>
              <DialogContentText>
                Organize your patients into lists to get the most out of your experience.
              </DialogContentText>
              <form noValidate>
                <FormGroup style={{ margin: '30px auto 30px', width: '80%' }}>
                  {Boolean(patientLists && patientLists.length) &&
                    patientLists.map((list, index) => {
                      return (
                        <FormControlLabel
                          className={classes.listBorder}
                          control={
                            <AdminCheckbox
                              checked={listIds && listIds.includes(list.id)}
                              onChange={handleChange}
                              name={String(list.id)}
                            />
                          }
                          label={list.list_name}
                          key={index}
                        />
                      );
                    })}
                  <AddPatientListDialog
                    invisible={false}
                    size={'small'}
                    refetch={refetchInternal}
                  />
                </FormGroup>

                <FormHelperText style={{ textAlign: 'center' }}>Patients may be on more than one list.</FormHelperText>
              </form>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            className={classes.cancelButton}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className={classes.addButton}
            disabled={saving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default AddPatientToListDialog;
